import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, LoadingSpinner, Button, HeadingText } from '@paypalcorp/pp-react';
import { Message } from '@paypalcorp/worldready-react';
import { urlConstants } from '../../common/constants';
import { AUTH_CONFIRMATION_PROMPT } from '../../common/storeConstants';
import { authConfirmationPromptFPTI } from '../../helpers/analytics/homePageAnalytics';
import { trackClickEvent, trackActivityEvent } from '../../helpers/analytics/fptiHelper';
import { authConfirmWithYes, authConfirmWithNo, closeWindow } from './AuthConfirmationPrompt-Actions';

const AuthConfirmationPrompt = function () {
  const dispatch = useDispatch();
  const { display, decided, authorized, isSending, error } = useSelector((state) => state[AUTH_CONFIRMATION_PROMPT]);

  useEffect(() => {
    if (display) {
      // FPTI - display auth confirmation prompt
      trackActivityEvent(authConfirmationPromptFPTI.authPromptShown);
    }
  }, [display]);

  useEffect(() => {
    if (error) {
      // FPTI - auth confirmation failure
      trackActivityEvent(authConfirmationPromptFPTI.authPromptError);
    }
  }, [error]);

  // 'Close' modal click handler
  const clickHandlerClose = () => {
    if (display) {
      dispatch(closeWindow());
      trackClickEvent(authConfirmationPromptFPTI.authPromptOnClickClose);
    }
  };

  // 'Yes' button click handler
  const clickHandlerYes = () => {
    dispatch(authConfirmWithYes());
    trackClickEvent(authConfirmationPromptFPTI.authPromptOnClickYes);
  };

  // 'No' button click handler
  const clickHandlerNo = () => {
    dispatch(authConfirmWithNo());
    trackClickEvent(authConfirmationPromptFPTI.authPromptOnClickNo);
  };

  // Render error message if the auth service failed
  const renderAuthFailed = () => (
    <div
      className="sh_auth_confirmation_prompt__container sh_auth_confirmation_prompt__failure"
      data-testid="sh-auth-confirm-prompt-failure-container"
    >
      <HeadingText size="sm" className="sh_auth_confirmation_prompt__failure--header">
        <div className="sh_auth_confirmation_prompt__failure--message">
          <Message id="smarthelp.app.authConfirmation.failureMessage.label" />
        </div>
        <div className="sh_auth_confirmation_prompt__failure--message">
          <Message
            id="smarthelp.app.authConfirmation.notOnPhone.label"
            // eslint-disable-next-line react/jsx-no-bind
            // eslint-disable-next-line react/no-unstable-nested-components
            link={(innerMsg) => <a href={urlConstants.CONTACT_US_URL}>{innerMsg}</a>}
          />
        </div>
      </HeadingText>
      <Button className="sh_auth_confirmation_prompt__container--btn" onClick={() => clickHandlerClose()} secondary>
        <Message id="smarthelp.app.authConfirmation.closeWindow.label" />
      </Button>
    </div>
  );

  // Render 'Thank you' or additional help message
  const renderConfirmation = () => (
    <div className="sh_auth_confirmation_prompt__container" data-testid="sh-auth-confirm-prompt-confirmation-container">
      <HeadingText size="sm" className="sh_auth_confirmation_prompt__container--header">
        {authorized ? (
          <Message id="smarthelp.app.app.thankYouLabel" />
        ) : (
          <Message
            id="smarthelp.app.authConfirmation.notOnPhone.label"
            // eslint-disable-next-line react/jsx-no-bind
            // eslint-disable-next-line react/no-unstable-nested-components
            link={(innerMsg) => <a href={urlConstants.CONTACT_US_URL}>{innerMsg}</a>}
          />
        )}
      </HeadingText>
      <Button className="sh_auth_confirmation_prompt__container--btn" onClick={() => clickHandlerClose()} secondary>
        <Message id="smarthelp.app.authConfirmation.closeWindow.label" />
      </Button>
    </div>
  );

  // Render verification UI with YES/NO button to confirm and verify
  const renderVerificationInfo = () => (
    <div
      className="sh_auth_confirmation_prompt__container--verify"
      data-testid="sh-auth-confirm-prompt-verify-container"
    >
      <HeadingText size="sm" className="sh_auth_confirmation_prompt__container--header--verify">
        <Message id="smarthelp.app.authConfirmation.headerLabel" />
      </HeadingText>
      <div className="sh_auth_confirmation_prompt__container--verify-label">
        <Message id="smarthelp.app.authConfirmation.subLabel" />
      </div>
      <div className="sh_auth_confirmation_prompt__container--verify--btns">
        <Button className="sh_auth_confirmation_prompt__container--verify--btn" onClick={() => clickHandlerYes()}>
          <Message id="smarthelp.app.app.yesLabel" />
        </Button>
        <Button
          className="sh_auth_confirmation_prompt__container--verify--btn"
          onClick={() => clickHandlerNo()}
          secondary
        >
          <Message id="smarthelp.app.app.noLabel" />
        </Button>
      </div>
    </div>
  );

  const renderConfirmAuthPrompt = () => {
    // render the confirmation auth prompt only when display is true
    if (!display) {
      return null;
    }

    // Render Spinner after selecting 'Yes/No' and waiting for auth request
    if (isSending) {
      return (
        <div className="sh_auth_confirmation_prompt__spinner" data-testid="sh-auth-confirm-prompt-spinner">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return renderAuthFailed();
    }

    if (!decided) {
      return renderVerificationInfo();
    }

    return renderConfirmation();
  };

  return (
    <div>
      <Dialog
        hideTitle
        title=""
        isOpen={display}
        onClose={() => clickHandlerClose()}
        headerClassName={!decided ? 'sh_auth_confirmation_prompt__dialog' : ''}
      >
        <div className="sh_auth_confirmation_prompt" data-testid="sh-auth-confirm-prompt-container">
          {renderConfirmAuthPrompt()}
        </div>
      </Dialog>
    </div>
  );
};

export default AuthConfirmationPrompt;
